import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Panel } from "../components/layout/Panel";
import {
  getAlertForService,
  listChannelsForService,
  updateAlertForService,
  type Alert as AlertType,
  type ServiceAlertChannel,
} from "../api/client";
import { useHierarchyContext } from "../contexts/HierarchyContext";

export function PageAlertEdit() {
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const { alertId } = useParams<{ alertId: string }>();
  const navigate = useNavigate();
  const [alert, setAlert] = useState<(AlertType & { channel_ids: string[] }) | null>(null);
  const [channels, setChannels] = useState<ServiceAlertChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!serviceSlug || !alertId) return;
    const abort = new AbortController();
    setLoading(true);
    setError(null);
    Promise.all([
      getAlertForService(serviceSlug, alertId, abort.signal),
      listChannelsForService(serviceSlug, abort.signal),
    ])
      .then(([a, chans]) => {
        if (abort.signal.aborted) return;
        setAlert(a);
        setChannels(chans);
      })
      .catch((err) => {
        if (abort.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Failed to load alert");
      })
      .finally(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => abort.abort();
  }, [serviceSlug, alertId]);

  const handleToggleAlert = async (enabled: boolean) => {
    if (!serviceSlug || !alertId || !alert) return;
    try {
      await updateAlertForService(serviceSlug, alertId, { enabled }, undefined);
      setAlert((prev) => (prev ? { ...prev, enabled } : null));
    } catch {
      // ignore
    }
  };

  const handleChannelsChange = async (channelIds: string[]) => {
    if (!serviceSlug || !alertId) return;
    try {
      await updateAlertForService(serviceSlug, alertId, { channel_ids: channelIds }, undefined);
      setAlert((prev) => (prev ? { ...prev, channel_ids: channelIds } : null));
    } catch {
      // ignore
    }
  };

  if (!serviceSlug || !alertId) {
    return (
      <Alert severity="warning">No Service or Alert in URL.</Alert>
    );
  }

  if (loading || !alert) {
    return <Typography>Loading…</Typography>;
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Panel title={`Alert: ${alert.name}`}>
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {alert.source_type} · {alert.condition_type}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Switch
              size="small"
              checked={alert.enabled}
              onChange={(_, checked) => handleToggleAlert(checked)}
            />
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {alert.enabled ? "Enabled" : "Disabled"}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Notification channels
        </Typography>
        <FormControl size="small" fullWidth sx={{ mb: 3 }}>
          <InputLabel>Channels</InputLabel>
          <Select
            multiple
            value={Array.isArray(alert.channel_ids) ? alert.channel_ids : []}
            label="Channels"
            onChange={(e) => {
              const v = e.target.value;
              handleChannelsChange(typeof v === "string" ? v.split(",") : v);
            }}
            renderValue={(selected) =>
              (selected as string[])
                .map((id) => channels.find((c) => c.id === id)?.name || channels.find((c) => c.id === id)?.channel_type || id)
                .join(", ") || "None"
            }
            MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
          >
            {channels.map((c) => {
              const selected = Array.isArray(alert.channel_ids) ? alert.channel_ids : [];
              return (
                <MenuItem key={c.id} value={c.id}>
                  <ListItemIcon>
                    <Checkbox checked={selected.indexOf(c.id) > -1} />
                  </ListItemIcon>
                  <ListItemText primary={c.name || c.channel_type} />
                </MenuItem>
              );
            })}
            {channels.length === 0 && (
              <MenuItem disabled>
                <Typography variant="body2" sx={{ opacity: 0.6 }}>
                  No channels — create one on the alerts page
                </Typography>
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Panel>

      <Box sx={{ mt: 2 }}>
        <Button
          variant="text"
          onClick={() => navigate(`/services/${encodeURIComponent(serviceSlug!)}/alerts`)}
        >
          Back to alerts
        </Button>
      </Box>
    </Box>
  );
}
